import { useState } from "react";
import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import Button from "@components/ui/buttons/Button";
import TextField from "@components/ui/textfields/TextField";
import { raiseError } from "context/error.service";
import { forgotPassword } from "@services/api/auth";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ForgotPasswordModal({
  onClose,
  initialEmail = "",
}: {
  onClose: () => void;
  initialEmail?: string;
}) {
  const [email, setEmail] = useState(initialEmail);
  const [isLoading, setIsLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async () => {
    const trimmed = email.trim();
    if (!EMAIL_REGEX.test(trimmed)) {
      raiseError({
        type: "Email invalide",
        reason: "Veuillez entrer une adresse email valide",
        solutions: ["Vérifiez le format de votre adresse email"],
      });
      return;
    }

    setIsLoading(true);
    try {
      await forgotPassword(trimmed);
      setDone(true);
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 429) {
        raiseError({
          type: "Trop de tentatives",
          reason:
            error?.response?.data?.detail ||
            "Trop de tentatives. Veuillez réessayer dans quelques minutes.",
          solutions: ["Patientez quelques minutes avant de réessayer"],
        });
      } else {
        raiseError({
          type: "Erreur",
          reason:
            "Impossible d'envoyer l'email de réinitialisation pour le moment.",
          solutions: ["Vérifiez votre connexion et réessayez"],
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <BaseModal
      title="Mot de passe oublié"
      startIcon={<Icon1 name="user" size={20} />}
      onClose={onClose}
      width="max-w-md"
    >
      {done ? (
        <div className="bg-white flex flex-col items-center gap-4 p-6 text-center">
          <div className="border p-4 rounded-full flex items-center justify-center">
            <Icon1 name="done" size={40} color="#10B981" />
          </div>
          <div className="text-hx2 font-bold">Email envoyé</div>
          <div className="text-h3 text-neutral-400">
            Si un compte existe avec cette adresse, vous recevrez un email
            contenant un lien pour choisir un mot de passe.
          </div>
          <div className="text-h3 text-neutral-400">
            Même si vous vous êtes inscrit avec Google, ce lien vous permet
            d'ajouter un mot de passe pour vous connecter avec votre email.
          </div>
          <Button variant="large" label="Fermer" onClick={onClose} />
        </div>
      ) : (
        <form
          className="bg-white flex flex-col gap-4 p-6"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <div className="text-h3 text-neutral-400">
            Entrez votre adresse email. Si un compte existe, vous recevrez un
            lien pour choisir un nouveau mot de passe.
          </div>
          <TextField
            type="email"
            label="Email"
            placeholder="marie.curie@health.com"
            value={email}
            onChange={(value) => setEmail(value)}
            disabled={isLoading}
          />
          <Button
            variant="large"
            type="submit"
            disabled={isLoading}
            loading={isLoading}
            label={isLoading ? "Envoi..." : "Envoyer le lien"}
          />
        </form>
      )}
    </BaseModal>
  );
}
